import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Check, ArrowRight, Award, BookOpen, Shield, GraduationCap } from 'lucide-react';
import ShareResult from '../ShareResult';

const CERTIFICATIONS = [
  { id: 'issa', name: 'ISSA', cost: 799, label: 'International Sports Sciences Association', popular: true },
  { id: 'nasm', name: 'NASM', cost: 899, label: 'National Academy of Sports Medicine', popular: true },
  { id: 'ace', name: 'ACE', cost: 849, label: 'American Council on Exercise', popular: false },
  { id: 'nsca', name: 'NSCA-CPT', cost: 435, label: 'National Strength & Conditioning Association', popular: false },
  { id: 'acsm', name: 'ACSM-CPT', cost: 549, label: 'American College of Sports Medicine', popular: false },
];

const ADD_ONS = [
  { id: 'study', name: 'Study Materials (textbooks, prep courses)', cost: 200, default: true },
  { id: 'cpr', name: 'CPR / AED Certification', cost: 75, default: true },
  { id: 'insurance', name: 'Liability Insurance (annual)', cost: 200, default: false },
  { id: 'continuing', name: 'Continuing Education (2-year cycle)', cost: 300, default: false },
  { id: 'business', name: 'Business Formation (LLC, website)', cost: 500, default: false },
];

export default function PersonalTrainerCostArticle() {
  const [selectedCert, setSelectedCert] = useState('nasm');
  const [addOns, setAddOns] = useState(() => {
    const init = {};
    ADD_ONS.forEach(a => { init[a.id] = a.default; });
    return init;
  });

  const cert = CERTIFICATIONS.find(c => c.id === selectedCert) || CERTIFICATIONS[1];
  const addOnTotal = useMemo(() => ADD_ONS.reduce((s, a) => s + (addOns[a.id] ? a.cost : 0), 0), [addOns]);
  const total = cert.cost + addOnTotal;

  const toggleAddOn = (id) => setAddOns(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <main className="pt-20 min-h-screen bg-[#0a0a0a]" data-testid="pt-cost-article">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors mb-4 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Back to Blog</Link>

        <h1 className="text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="pt-cost-title">How Much Does It Cost to Become a Personal Trainer?</h1>
        <p className="text-zinc-400 text-lg mb-8 leading-relaxed">A comprehensive breakdown of every cost on your path to certification — from exam fees to insurance and beyond.</p>

        {/* Key Takeaways */}
        <div className="bg-zinc-900/60 border border-zinc-800 p-5 mb-10">
          <h3 className="text-white text-sm uppercase tracking-wider mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Key Takeaways</h3>
          <ul className="space-y-2">
            {['Certification programs range from $200 to over $2,000', 'ISSA, NASM, and ACE are the most recognized certifications', 'Additional costs include CPR cert, insurance, and continuing education', 'Total investment typically ranges $800 - $2,500'].map((t, i) => (
              <li key={t} className="flex items-start gap-2 text-zinc-300 text-sm">
                <Check className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Interactive Cost Calculator */}
        <div className="border border-[#22c55e]/30 bg-[#22c55e]/5 p-6 mb-10" data-testid="pt-cost-calculator">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-5 h-5 text-[#22c55e]" />
            <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>PT Cost Calculator</h3>
          </div>
          <p className="text-zinc-500 text-xs mb-5">Select your certification and add-ons to estimate your total investment.</p>

          {/* Certification Selection */}
          <div className="mb-5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-2">Choose Certification</label>
            <div className="space-y-2">
              {CERTIFICATIONS.map(c => (
                <button key={c.id}
                  onClick={() => setSelectedCert(c.id)}
                  className={`w-full flex items-center justify-between p-3 border transition-all text-left ${selectedCert === c.id ? 'border-[#22c55e]/60 bg-[#22c55e]/10' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}
                  data-testid={`cert-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <GraduationCap className="w-4 h-4" style={{ color: selectedCert === c.id ? '#22c55e' : '#52525b' }} />
                    <div>
                      <span className="text-white text-sm">{c.name}</span>
                      {c.popular && <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 uppercase">Popular</span>}
                      <p className="text-zinc-600 text-[10px]">{c.label}</p>
                    </div>
                  </div>
                  <span className="text-white text-sm font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>${c.cost.toLocaleString()}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Add-ons */}
          <div className="mb-5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-2">Additional Expenses</label>
            <div className="space-y-2">
              {ADD_ONS.map(a => (
                <button key={a.id}
                  onClick={() => toggleAddOn(a.id)}
                  className={`w-full flex items-center justify-between p-3 border transition-all text-left ${addOns[a.id] ? 'border-[#00F0FF]/40 bg-[#00F0FF]/5' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}
                  data-testid={`addon-${a.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 border flex items-center justify-center ${addOns[a.id] ? 'border-[#00F0FF] bg-[#00F0FF]/20' : 'border-zinc-700'}`}>
                      {addOns[a.id] && <Check className="w-3 h-3 text-[#00F0FF]" />}
                    </div>
                    <span className="text-zinc-300 text-sm">{a.name}</span>
                  </div>
                  <span className="text-zinc-400 text-sm">+${a.cost}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="bg-zinc-900/60 border border-zinc-800 p-5 text-center" data-testid="cost-total">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Estimated Total Investment</p>
            <p className="text-5xl text-[#22c55e]" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>${total.toLocaleString()}</p>
            <div className="flex justify-center gap-4 mt-3 text-[10px] text-zinc-600">
              <span>Certification: ${cert.cost}</span>
              <span>Add-ons: ${addOnTotal}</span>
            </div>
            <div className="mt-3">
              <ShareResult
                text={`Becoming a Personal Trainer costs ~$${total.toLocaleString()} with ${cert.name} certification. Calculate yours:`}
                url="https://ninjathlete.com/blogs/article/how-much-does-it-cost-to-become-a-personal-trainer"
                label="Share Estimate"
              />
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="prose-ninja space-y-6 text-zinc-300 leading-relaxed mb-10">
          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Which Certification is Best?</h2>
          <p>The best certification depends on your goals. <strong className="text-white">NASM</strong> is known for evidence-based approach and corrective exercise. <strong className="text-white">ISSA</strong> is flexible and affordable with strong online options. <strong className="text-white">ACE</strong> focuses on practical application. <strong className="text-white">NSCA</strong> is popular with strength and conditioning coaches working with athletes.</p>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Steps to Get Certified</h2>
          <ol className="space-y-3">
            {[
              'Choose a certification program that fits your goals and budget',
              'Enroll in the program and pay required fees',
              'Study the course materials and complete practice exams',
              'Schedule and pass the certification exam',
              'Maintain certification with continuing education credits',
            ].map((step, i) => (
              <li key={step} className="flex items-start gap-3">
                <span className="w-6 h-6 flex items-center justify-center border border-[#22c55e]/40 text-[#22c55e] text-xs flex-shrink-0" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{i + 1}</span>
                <span className="text-sm">{step}</span>
              </li>
            ))}
          </ol>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Conclusion</h2>
          <p>Investing in your education and certification is an investment in your future. With careful planning, you can find a program that fits your budget and sets you on the path to a rewarding career in the fitness industry. Total costs typically range from <strong className="text-white">$800 to $2,500</strong> depending on your choices.</p>
        </div>

        {/* Cross-links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Link to="/blogs/article/how-to-train-like-an-athlete" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#22c55e]/40 p-4 transition-all" data-testid="crosslink-train">
            <h4 className="text-white text-sm group-hover:text-[#22c55e] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>How to Train Like an Athlete</h4>
            <p className="text-zinc-600 text-xs mt-1">Comprehensive training guide</p>
          </Link>
          <Link to="/tools" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#00F0FF]/40 p-4 transition-all" data-testid="crosslink-tools">
            <h4 className="text-white text-sm group-hover:text-[#00F0FF] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Fitness Tools</h4>
            <p className="text-zinc-600 text-xs mt-1">Calculators & trackers</p>
          </Link>
        </div>
      </div>
    </main>
  );
}
